import { Link } from "react-router-dom";
import { Facebook, Instagram, MapPin, Mail, Phone, ExternalLink } from "lucide-react";
import TikTokIcon from "@/components/TikTokIcon";
import { useSite } from "@/context/SiteContext";

const LOGO = `${process.env.PUBLIC_URL}/logo.png`;

const ministryLinks = [
  { label: "David Oyedepo Ministries", href: "https://faithtabernacle.org.ng/" },
  { label: "Faith Oyedepo Ministries", href: "http://www.faithoyedepo.org/" },
  { label: "Covenant University", href: "https://www.covenantuniversity.edu.ng/" },
  { label: "Landmark University", href: "https://lmu.edu.ng/" },
  { label: "Dominion Bookstore", href: "https://www.dphstore.co.uk/" },
];

const PRIVACY_PDF = "https://winners-chapel.org.uk/wp-content/uploads/2018/05/Privacy-Policy-WMA-WCI-Rev1.0.pdf";

export default function Footer() {
  const { content } = useSite();
  const c = content || {};

  return (
    <footer data-testid="site-footer" className="relative border-t border-white/10 bg-sec-2">
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={LOGO} alt="Winners Chapel logo" className="h-12 w-auto object-contain" />
              <span className="font-heading font-extrabold text-white text-lg leading-tight">
                Winners Chapel<br />International Colchester
              </span>
            </Link>
            <p className="text-zinc-300 max-w-md leading-relaxed">
              {c.tagline || "Come and experience the Divine Touch of God."}
            </p>
            <p className="text-zinc-400 text-sm mt-2">A branch of Living Faith Church Worldwide.</p>
            <div className="flex gap-3 mt-6">
              {c.facebook && (
                <a href={c.facebook} target="_blank" rel="noreferrer" data-testid="footer-facebook"
                  className="w-10 h-10 grid place-items-center rounded-full bg-white/10 border border-white/10 hover:bg-chapel-red hover:text-white text-white transition-colors duration-300" aria-label="Facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {c.instagram && (
                <a href={c.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram"
                  className="w-10 h-10 grid place-items-center rounded-full bg-white/10 border border-white/10 hover:bg-chapel-red hover:text-white text-white transition-colors duration-300" aria-label="Instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {c.tiktok && (
                <a href={c.tiktok} target="_blank" rel="noreferrer" data-testid="footer-tiktok"
                  className="w-10 h-10 grid place-items-center rounded-full bg-white/10 border border-white/10 hover:bg-chapel-red hover:text-white text-white transition-colors duration-300" aria-label="TikTok">
                  <TikTokIcon className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          {/* Explore */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">Explore</h4>
            <ul className="space-y-2.5 text-zinc-300 text-sm">
              <li><Link to="/about" className="hover:text-chapel-red transition-colors">About Us</Link></li>
              <li><Link to="/service-times" className="hover:text-chapel-red transition-colors">Service Times</Link></li>
              <li><Link to="/calendar" className="hover:text-chapel-red transition-colors">Calendar</Link></li>
              <li><Link to="/gallery" className="hover:text-chapel-red transition-colors">Gallery</Link></li>
              <li><Link to="/contact" className="hover:text-chapel-red transition-colors">Plan a Visit</Link></li>
              <li><Link to="/#give" data-testid="footer-give-link" className="hover:text-chapel-red transition-colors">Give</Link></li>
              <li><Link to="/#prayer" className="hover:text-chapel-red transition-colors">Prayer Request</Link></li>
              <li><Link to="/wsf" className="hover:text-chapel-red transition-colors">WSF Resources</Link></li>
              <li><Link to="/testimonies" className="hover:text-chapel-red transition-colors">Testimonies</Link></li>
              <li><Link to="/covenant-hour" className="hover:text-chapel-red transition-colors">Covenant Hour of Prayer</Link></li>
              <li>
                <a href={PRIVACY_PDF} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-chapel-red transition-colors">
                  Privacy Policy <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>

          {/* Ministry Links */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">Ministry Links</h4>
            <ul className="space-y-2.5 text-zinc-300 text-sm">
              {ministryLinks.map((m) => (
                <li key={m.href}>
                  <a href={m.href} target="_blank" rel="noreferrer" data-testid={`ministry-link-${m.label.toLowerCase().replace(/ /g, "-")}`}
                    className="inline-flex items-center gap-1 hover:text-chapel-red transition-colors">
                    {m.label} <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Find Us */}
          <div className="lg:col-span-2">
            <h4 className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400 mb-4">Find Us</h4>
            <ul className="space-y-3 text-zinc-300 text-sm">
              <li className="flex gap-2.5">
                <MapPin className="w-4 h-4 text-chapel-red shrink-0 mt-0.5" />
                <span>{c.address || "2 Balkerne Hill, Colchester, Essex CO3 3AA"}</span>
              </li>
              {c.phone && (
                <li className="flex gap-2.5 items-center">
                  <Phone className="w-4 h-4 text-chapel-red shrink-0" />
                  <a href={`tel:${c.phone}`} className="hover:text-white">{c.phone}</a>
                </li>
              )}
              {c.email && (
                <li className="flex gap-2.5 items-center">
                  <Mail className="w-4 h-4 text-chapel-red shrink-0" />
                  <a href={`mailto:${c.email}`} className="hover:text-white break-all">{c.email}</a>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-zinc-400">
          <p>© {new Date().getFullYear()} Winners Chapel International Colchester. All rights reserved.</p>
          <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-white transition-colors">
            Admin Login
          </Link>
        </div>
      </div>
    </footer>
  );
}
